import { formatDate, resolveLocale } from './formatDate';

/**
 * Gewichte werden immer in Kilogramm gespeichert und uebertragen.
 *
 * Erfasst wird aber unterschiedlich: bei Welpen zaehlen einzelne Gramm (Zunahme
 * pro Tag), bei erwachsenen Hunden reicht Kilogramm. Deshalb haengt an jedem
 * Eintrag die Einheit, in der er erfasst wurde - sie steuert Eingabe und Anzeige,
 * nicht den gespeicherten Wert.
 */

export type WeightUnit = 'g' | 'kg';

/** Bis zu diesem Alter ist Gramm die sinnvollere Einheit (Nestlingsphase + Abgabezeit). */
const PUPPY_AGE_DAYS = 12 * 7;

/** Alles ausser "g" gilt als Kilogramm - auch Altbestand ohne Einheit. */
export const normalizeUnit = (unit: string | null | undefined): WeightUnit =>
  unit?.trim().toLowerCase() === 'g' ? 'g' : 'kg';

/** Eingabewert -> Kilogramm (Speicherformat). */
export const toKilograms = (value: number, unit: WeightUnit): number =>
  unit === 'g' ? value / 1000 : value;

/** Kilogramm -> Anzeigewert in der gewuenschten Einheit. */
export const fromKilograms = (kilograms: number, unit: WeightUnit): number =>
  unit === 'g' ? kilograms * 1000 : kilograms;

/** Schrittweite des Zahlenfeldes: 1 g bzw. 10 g. */
export const weightInputStep = (unit: WeightUnit): string => (unit === 'g' ? '1' : '0.01');

/**
 * Formatiert ein gespeichertes Gewicht in der Einheit, in der es erfasst wurde.
 * Gramm ohne Nachkommastellen, Kilogramm mit bis zu drei (= 1 g Aufloesung).
 */
export const formatWeight = (kilograms: number, unit?: string | null): string => {
  const target = normalizeUnit(unit);
  const value = fromKilograms(kilograms, target);
  const formatted = value.toLocaleString(resolveLocale(), {
    minimumFractionDigits: 0,
    maximumFractionDigits: target === 'g' ? 0 : 3
  });
  return `${formatted} ${target}`;
};

/** Vorschlag fuer die Erfassungseinheit anhand des Alters - junge Hunde in Gramm. */
export const suggestWeightUnit = (dateOfBirth: string | null | undefined): WeightUnit => {
  if (!dateOfBirth) return 'kg';
  const birth = new Date(dateOfBirth);
  if (Number.isNaN(birth.getTime())) return 'kg';
  const ageInDays = (Date.now() - birth.getTime()) / (1000 * 60 * 60 * 24);
  return ageInDays >= 0 && ageInDays <= PUPPY_AGE_DAYS ? 'g' : 'kg';
};

export type WeightGroupPeriod = 'week' | 'month';

/**
 * Welche Gruppierung zur Messdichte passt: bei taeglichem Woegen (Welpen) Kalenderwochen, sonst Monate - bei
 * woechentlichem oder selteneren Woegen waere jede Woche sonst eine eigene Gruppe mit nur einer Messung.
 * Massgebend ist der mittlere (Median-)Abstand zwischen zwei Messungen.
 */
export function weightGroupPeriod(entries: { measuredAt: string }[]): WeightGroupPeriod {
  if (entries.length < 2) return 'month';
  const times = entries.map((e) => new Date(e.measuredAt).getTime()).sort((a, b) => a - b);
  const gaps = times.slice(1).map((time, i) => (time - times[i]) / (1000 * 60 * 60 * 24)).sort((a, b) => a - b);
  const median = gaps[Math.floor(gaps.length / 2)];
  return median < 3.5 ? 'week' : 'month';
}

/**
 * Fasst Eintraege nach Kalendermonat zusammen (Beschriftung z.B. "September 2026").
 * Erwartet `entriesDescending` neueste-zuerst; die Reihenfolge bleibt erhalten.
 */
export function groupByMonth<T extends { measuredAt: string }>(entriesDescending: T[]): WeightWeekGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const entry of entriesDescending) {
    const d = new Date(entry.measuredAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(entry);
    else groups.set(key, [entry]);
  }
  const monthFormat = new Intl.DateTimeFormat(resolveLocale(), { month: 'long', year: 'numeric' });
  return Array.from(groups.entries()).map(([key, entries]) => ({
    key,
    rangeLabel: monthFormat.format(new Date(`${key}-01T00:00:00`)),
    entries
  }));
}

export interface WeightWeekGroup<T> {
  key: string;
  rangeLabel: string;
  /** Neueste zuerst, wie in `entriesDescending`. */
  entries: T[];
}

const startOfWeek = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const mondayIndex = (d.getDay() + 6) % 7; // Montag = 0
  d.setDate(d.getDate() - mondayIndex);
  return d;
};

/**
 * Fasst Eintraege nach Kalenderwoche (Montag-Sonntag) zusammen.
 * Bei taeglichem Woelfen sind das sonst schnell dutzende Einzeleintraege in der Liste.
 * Erwartet `entriesDescending` bereits neueste-zuerst sortiert; Reihenfolge bleibt
 * sowohl fuer die Gruppen als auch je Gruppe fuer die Eintraege erhalten.
 */
export function groupByWeek<T extends { measuredAt: string }>(entriesDescending: T[]): WeightWeekGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const entry of entriesDescending) {
    // Lokaler Schluessel statt toISOString(): startOfWeek liefert lokale Mitternacht, die in Zeitzonen oestlich
    // von UTC (z.B. Schweiz) als UTC noch auf dem Vortag liegt - die Woche wuerde dann So-Sa statt Mo-So beschriftet.
    const monday = startOfWeek(new Date(entry.measuredAt));
    const key = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(entry);
    else groups.set(key, [entry]);
  }

  return Array.from(groups.entries()).map(([key, entries]) => {
    const weekStart = new Date(`${key}T00:00:00`);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return {
      key,
      rangeLabel: `${formatDate(weekStart)} – ${formatDate(weekEnd)}`,
      entries
    };
  });
}
